import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import {
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { listUsers, usersByCompanyID } from "graphql/queries";
import usePermissions from "hooks/usePermission";
import UserUpdateForm from "./UserUpdateForm";
import { getCompanyName } from "graphql/custom-queries";
import { LayoutContext } from "layout/context/LayoutContext";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";

const Users = () => {
  const { me } = useContext(LayoutContext);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [canUpdate] = usePermissions(["user:update"]);
  const [modalUser, setModalUser] = useState({
    visible: false,
    userId: null,
  });
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      users.map((item) => ({
        ...item,
        companyName: item.companyID ? item.companyName || <Loader /> : "",
        actions: (
          <>
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.actions.edit}
                variation="link"
                size="small"
                onClick={() => openModal(item.id)}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users]
  );

  const openModal = (id) => setModalUser({ visible: true, userId: id });

  const onNext = () => {
    fetchUsers(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchUsers(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchUsers(undefined, undefined, keywordSplit);
  };

  const onSuccessAction = () => {
    setModalUser({ visible: false });
    fetchUsers();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByUsers = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setUsers((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };
  async function fetchUsers(token, isNext = true, keywords) {
    let objOperation = {};
    let nameOperation = listUsers;
    let nameOperationStr = "listUsers";
    try {
      setUsersLoading(true);

      if (!!searchBy.company.id || !!me.companyID) {
        objOperation = {
          companyID: searchBy.company.id || me.companyID,
        };
        nameOperation = usersByCompanyID;
        nameOperationStr = "usersByCompanyID";
      }

      const usersData = await API.graphql(
        graphqlOperation(nameOperation, {
          ...objOperation,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      email: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      const usersItems = usersData.data[nameOperationStr].items;
      setUsers(usersItems);
      getNamesByUsers(usersItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: usersData.data[nameOperationStr].nextToken,
        };
      });
      setUsersLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setUsersLoading(false);
    }
  }

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "40%" },
    },
    {
      key: "email",
      label: strings.table.headers.email,
      _style: { width: "30%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "30%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {usersLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
      </div>
      {modalUser.userId && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalUser.visible}
          onClose={() => setModalUser({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>{strings.modalUser.updateTitle}</CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <UserUpdateForm
                userId={modalUser.userId}
                onSuccess={onSuccessAction}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Usuarios",
    responses: {
      errorFetch: "Error al consultar usuarios",
    },
    modalUser: {
      updateTitle: "Actualizar Usuario",
    },
    table: {
      headers: {
        companyName: "Empresa",
        email: "Correo electrónico",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    actions: {
      edit: "Editar",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Users;
